import {
  RENDER_HEALTH_THRESHOLD,
  RENDER_HEALTH_WINDOW_MS,
  type RenderHealthState,
} from './renderHealth';

const INIT_CRASH_KEY = 'baobab_init_crash_count';

export const INIT_CRASH_THRESHOLD = 3;
export const INIT_CRASH_WINDOW_MS = 600_000;

export interface CrashState {
  count: number;
  firstFailureAt: number;
}

export type RecoveryDecision =
  | { kind: 'none' }
  | {
      kind: 'revert';
      reason: 'failed_bundle' | 'repeated_failures' | 'render_failures';
    };

export const readCrashState = (): CrashState | null => {
  try {
    const raw = localStorage.getItem(INIT_CRASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CrashState>;
    if (
      typeof parsed?.count !== 'number' ||
      typeof parsed?.firstFailureAt !== 'number'
    ) {
      return null;
    }
    return { count: parsed.count, firstFailureAt: parsed.firstFailureAt };
  } catch {
    // Corrupt or unavailable storage must never block boot.
    return null;
  }
};

const writeCrashState = (state: CrashState): void => {
  try {
    localStorage.setItem(INIT_CRASH_KEY, JSON.stringify(state));
  } catch {
    // Best-effort only.
  }
};

export const recordLaunch = (now: number): void => {
  const previous = readCrashState();
  if (!previous || now - previous.firstFailureAt > INIT_CRASH_WINDOW_MS) {
    writeCrashState({ count: 1, firstFailureAt: now });
    return;
  }
  writeCrashState({ count: previous.count + 1, firstFailureAt: previous.firstFailureAt });
};

// Do NOT move the clear later: clearing on local-init success is deliberate, since clearing after
// the blocking ATT modal made a dismissed prompt look like a boot failure.
export const clearCrashState = (): void => {
  try {
    localStorage.removeItem(INIT_CRASH_KEY);
  } catch {
    // Best-effort only.
  }
};

export const installBackgroundClear = (): void => {
  window.addEventListener('pagehide', clearCrashState);
};

export const decideRecovery = (input: {
  failedBundleId: string | null;
  currentBundleId: string;
  state: CrashState | null;
  renderHealth: RenderHealthState | null;
  now: number;
}): RecoveryDecision => {
  const { failedBundleId, currentBundleId, state, renderHealth, now } = input;

  if (failedBundleId && failedBundleId === currentBundleId) {
    return { kind: 'revert', reason: 'failed_bundle' };
  }

  if (
    state &&
    state.count >= INIT_CRASH_THRESHOLD &&
    now - state.firstFailureAt <= INIT_CRASH_WINDOW_MS
  ) {
    return { kind: 'revert', reason: 'repeated_failures' };
  }

  // Checked LAST: the plugin's own failure record and the local-init counter are both more
  // authoritative than a post-render heuristic, and either keeps its label when both would fire —
  // the render-health signal only adds coverage for the gap neither of the other two can see (a
  // bundle that boots fine and white-screens at React render; see renderHealth.ts's own doc
  // comment).
  if (
    renderHealth &&
    renderHealth.bundleId === currentBundleId &&
    renderHealth.count >= RENDER_HEALTH_THRESHOLD &&
    now - renderHealth.firstArmedAt <= RENDER_HEALTH_WINDOW_MS
  ) {
    return { kind: 'revert', reason: 'render_failures' };
  }

  return { kind: 'none' };
};
